<template>
  <div class="ti-dealer-selection" v-if="step.data">
    <Transition name="fade" mode="out-in" appear>
      <div class="ti-dealer-selection__choices" v-if="isUserAuthenticated">
        <template v-if="Object.keys(associatedDealer)?.length > 0">
          <p class="ti-dealer-selection__headline">
            {{ $t('i18n.dealerSelection.meSearch.copy') }}
            <br /><br />
            {{ $t('i18n.dealerSelection.meSearch.copy2') }}
          </p>

          <div class="ti-dealer-selection__choice">
            <DealerRadioCard
              :name="associatedDealer?.legalName"
              :value="true"
              icon="bds/mb-star/24"
              :selectedValue="dealerLocatorSelected"
              @selectedValueChanged="choiceSelected(associatedDealer)"
            >
              <template v-slot:infos>
                <h3>{{ associatedDealer?.legalName }}</h3>
                <span>
                  {{ currentStep.data?.dealerInfo?.address.line1 }} ·
                  {{ currentStep.data?.dealerInfo?.address.zipcode }}
                  {{ currentStep.data?.dealerInfo?.address.city }}
                </span>
              </template>
            </DealerRadioCard>
          </div>

          <div class="ti-dealer-selection__choice">
            <DealerRadioCard
              name="partner-selection"
              :value="false"
              :selectedValue="dealerLocatorSelected"
              @selectedValueChanged="choiceSelected()"
            >
              <template v-slot:infos>
                <p>Ich möchte einen anderen Mercedes-Benz Partner auswählen.</p>
              </template>
            </DealerRadioCard>
          </div>
        </template>

        <template v-else>
          <p class="ti-dealer-selection__headline">
            {{ $t('i18n.dealerSelection.dealer.headline') }}
          </p>
        </template>

        <NavigationComponent
          class="ti-dealer-selection__navigation"
          v-if="dealerLocatorSelected && currentStep.data?.dealerInfo"
          :step="step"
          :history="history"
          :key="step.stepId"
          :completed="true"
          @nextClicked="nextStep"
        />
      </div>

      <p v-else class="ti-dealer-selection__headline">
        {{ $t('i18n.dealerSelection.dealer.copy') }}
      </p>
    </Transition>

    <Transition name="fade" mode="out-in" appear>
      <DealerLocatorComponent
        v-if="!dealerLocatorSelected"
        class="ti-dealer-selection__dealer-locator"
        @selectedDealer="nextStep"
      />
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue';
import { Dealer } from '@owc/connection-wko-manager';
import { DealerInformation, Step } from '@tradein/api';
import DealerLocatorComponent from '../../components/dealer-locator/dealer-locator.vue';
import NavigationComponent from '../../components/navigation/navigation.vue';
import DealerRadioCard from '../../components/form-element/components/radio-card.vue';
import setup from '../../composables/setup';

interface Props {
  step: Step;
  history: Step[];
}

const props = defineProps<Props>();
const emit = defineEmits([
  'stepChangedAndNext',
  'navigateToStep',
  'prevClicked',
]);

const { wellKnownObjects, jweToken, configurationId } = setup();
const associatedDealer = ref<Partial<Dealer>>({});
const currentStep = ref<Step>({} as Step);
const dealerLocatorSelected = ref(false);
const isUserAuthenticated = ref(false);

watch(
  () => props.step,
  () => {
    currentStep.value = { ...props.step };
    isUserAuthenticated.value = jweToken.value !== undefined;
  },
  { immediate: true },
);

onMounted(() => {
  getAssociatedDealer();
});

const choiceSelected = (dealer?: Partial<Dealer>): void => {
  if (!dealer) {
    dealerLocatorSelected.value = false;
  } else {
    const mappedDealer: DealerInformation = {
      address: {
        city: `${dealer.address?.city}`,
        country: `${dealer.address?.countryIsoCode || (dealer.address as any).isoCode}`,
        zipcode: `${dealer.address?.zipCode}`,
        line1: `${dealer.address?.street} ${(dealer.address as any).streetNumber}`,
      },
      baseInfo: {
        outletId: `${dealer?.outletId}`,
        name1: `${dealer?.legalName}`,
      },
    };

    dealerLocatorSelected.value = true;

    currentStep.value.data.dealerInfo = mappedDealer;
  }
};

const nextStep = (dealer: DealerInformation): void => {
  if (dealer) {
    currentStep.value.data.dealerInfo = dealer;
  }

  currentStep.value.completed = true;

  emit('stepChangedAndNext', currentStep.value);
};

const getAssociatedDealer = () => {
  if (
    wellKnownObjects.value &&
    wellKnownObjects.value.User?.selectedAssociatedDealer?.salesDealer &&
    (configurationId.value === 'de-DE-mvp' ||
      configurationId.value === 'de-DE-one')
  ) {
    associatedDealer.value =
      wellKnownObjects.value.User?.selectedAssociatedDealer?.salesDealer;

    choiceSelected(associatedDealer.value);
  } else {
    dealerLocatorSelected.value = false;
  }
};
</script>

<style lang="scss">
.ti-dealer-selection {
  display: flex;
  flex-flow: row wrap;
  width: 100%;

  &__headline {
    @include wb-type-copy;
    @include wb-spacing('padding-bottom', 's');
  }

  &__choices {
    width: 100%;

    &.fade-enter-active {
      transition: opacity 400ms ease-in-out 0ms;
    }

    &.fade-leave-active {
      transition: opacity 200ms ease-in-out 0ms;
    }

    &.fade-enter-from,
    &.fade-leave-to {
      opacity: 0;
    }

    .ti-dealer-selection__navigation {
      justify-content: flex-end;

      .ti-navigation__cta-tertiary {
        display: none;
      }
    }
  }

  &__choice {
    @include wb-spacing('margin-bottom', 's');

    &:not(:last-child) {
      margin-bottom: 16px;
    }

    .radio-card {
      width: 100%;
      @include wb-spacing('margin-top', '3xs');

      label {
        position: relative;
        width: 100%;
        padding: 0;

        .wb-radio-control__indicator {
          position: absolute;
          left: inherit;
          right: 24px;
          top: 50%;
          transform: translateY(-50%);
        }

        input:checked ~ .radio-card__container {
          border-color: var(--highlight-color);
        }

        input:not(:checked):focus ~ .radio-card__container {
          border-color: var(--highlight-color);
          border-width: 3px;
        }
      }

      &__container {
        display: flex;
        @include wb-spacing('padding', 's');
        padding-right: 65px;
        border: 1px solid var(--wb-grey-70);
        border-radius: 4px;
      }

      &__infos {
        display: flex;
        flex-direction: column;
        justify-content: center;
        @include wb-spacing('padding-left', 'xxs');

        span {
          @include wb-type-copy;
          @include wb-spacing('margin-top', 'xxs');
          color: var(--wb-grey-20);
        }
      }

      &:hover {
        label {
          background-color: var(--wb-grey-95);
        }
      }
    }
  }

  wb-radio-control.radio-card:not([error]):not([disabled])
    > label
    > input:not(:checked):hover
    ~ .wb-radio-control__indicator {
    border-color: var(--wb-grey-70);
  }

  wb-radio-control > label > input:focus ~ .wb-radio-control__indicator {
    outline: none;
  }

  &__choice-description {
    @include wb-type-copy;
    color: var(--wb-grey-20);
    display: flex;
    align-items: center;

    wb-icon {
      align-self: flex-start;
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      margin-right: 4px;
      margin-top: 4px;

      @include breakpoint-from(mq4) {
        margin-top: 6px;
      }
    }
  }

  &__dealer-locator {
    &.fade-enter-active {
      transition: opacity 400ms ease-in-out 500ms; // Delay the transition to conceal the dealer locators initial flickering
    }

    &.fade-leave-active {
      transition: opacity 200ms ease-in-out 0ms;
    }

    &.fade-enter-from,
    &.fade-leave-to {
      opacity: 0;
    }
  }
}
</style>
